import { createAxiosClient, ServiceProxyMap } from '@atlassian/temp-client-utils';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import {
  CustomOrderEntitlement,
  CustomOrderRequest,
  GetOrderDetailsRequest,
  GetRewindOrderRequest,
  OrderIntentRequest,
  UpdateEntsOrderRequest,
  UpdateOrderRequest,
} from '@atlassian/cssxp-typescommon';
import { cloneDeep } from 'lodash';
import { validateAndReactivatePLSEntitlement } from './poa-api';

const client = createAxiosClient({ baseURL: '/api/1/oms' }, ServiceProxyMap.ANS);

export const updateEntitlementPlan = async (requestPayload: UpdateOrderRequest): Promise<any> => {
  const isPOAEnabled = requestPayload.isPOAEnabled;
  if (requestPayload?.operations?.length && requestPayload?.addPromotions?.length) {
    const cloneRequestPayload = cloneDeep(requestPayload);
    delete requestPayload.addPromotions;
    // removal process by patch entitlement
    await client.post('/order', requestPayload);
    delete cloneRequestPayload.operations;
    // add process by amendment entitlement
    // handle marketPlacePromo add
    const responseAdd = await client.post('/order', cloneRequestPayload);
    return responseAdd.data;
  }

  if (requestPayload?.orderType === OrderItemType.REACTIVATION_ORDER && requestPayload.isDeleted && isPOAEnabled) {
    const ccpReactivation = await validateAndReactivatePLSEntitlement(requestPayload);
    if (ccpReactivation.status === 200 || ccpReactivation.status === 202) {
      const response = await client.post('/order', requestPayload);
      return response.data;
    }
  }
  if (
    requestPayload?.orderType === OrderItemType.REACTIVATION_ORDER &&
    (requestPayload.transferTransactionAccountId || requestPayload.transferInvoiceGroupId)
  ) {
    // Reactivate order
    await client.post('/order', requestPayload);
    if (requestPayload.transferTransactionAccountId) {
      // Modify the transaction account and invoice group
      const modifyOrderRequestPayload: UpdateOrderRequest = {
        entitlementId: requestPayload.entitlementId,
        transferTransactionAccountId: requestPayload.transferTransactionAccountId,
        orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
        productId: requestPayload?.productId ?? '',
      };
      if (requestPayload.transferInvoiceGroupId) {
        modifyOrderRequestPayload.transferInvoiceGroupId = requestPayload?.transferInvoiceGroupId;
      }
      const modifiedResponseData = await client.post('/order', modifyOrderRequestPayload);
      return modifiedResponseData.data;
    }
    if (requestPayload.transferInvoiceGroupId) {
      // Modify the invoice group
      const modifiedInvoiceGroupData: UpdateOrderRequest = {
        entitlementId: requestPayload.entitlementId,
        invoiceGroupId: requestPayload.transferInvoiceGroupId,
        offeringId: requestPayload?.offeringId ?? undefined,
        productId: requestPayload?.productId ?? '',
      };
      const modifiedResponseData = await client.post('/order', modifiedInvoiceGroupData);
      return modifiedResponseData.data;
    }
  }

  const response = await client.post('/order', requestPayload);
  return response.data;
};

export const updateEntitlementsPlan = async (requestPayload: UpdateEntsOrderRequest): Promise<any> => {
  if (requestPayload?.operations?.length && requestPayload?.addPromotions?.length) {
    const cloneRequestPayload = cloneDeep(requestPayload);
    delete requestPayload.addPromotions;
    // removal process by patch entitlement
    await client.post('/orders', requestPayload);
    delete cloneRequestPayload.operations;
    // add process by amendment entitlement
    const responseAdd = await client.post('/orders', cloneRequestPayload);
    return responseAdd.data;
  }
  const response = await client.post('/orders', requestPayload);
  return response.data;
};

export const fetchOrderDetailsById = async (requestPayload: GetOrderDetailsRequest): Promise<any> => {
  const response = await client.get(`/getOrder/${requestPayload.orderId}/${requestPayload.transactionAccountId}`);
  return response.data;
};

export const fetchRewindOrderDetails = async (requestPayload: GetRewindOrderRequest): Promise<any> => {
  const response = await client.get(
    `/getRewindOrderRequest/${requestPayload.orderItemId}/${requestPayload.transactionAccountId}`
  );
  return response.data;
};

export const getBillEstimate = async (requestPayload: OrderIntentRequest): Promise<any> => {
  const response = await client.post('/getBillEstimate', requestPayload);
  return response.data;
};

export const getBillEstimateV2 = async (requestPayload: any): Promise<any> => {
  const response = await client.post('/getBillEstimateV2', requestPayload);
  return response.data;
};

export const getOrderPreview = async (requestPayload: OrderIntentRequest) => {
  const response = await client.post('/getOrderPreview', requestPayload);
  return response.data;
};

export const getOrderPreviewV2 = async (requestPayload: CustomOrderRequest) => {
  const response = await client.post('/getOrderPreviewV2', requestPayload);
  return response.data;
};

export const updateEntitlementPlanV2 = async (requestPayload: CustomOrderRequest): Promise<any> => {
  const entitlementsWithRemovePromotions: CustomOrderEntitlement[] = [];
  requestPayload?.entitlements?.forEach((entitlement) => {
    if (entitlement?.operations?.length) {
      const cloneEntitlement = cloneDeep(entitlement);
      delete cloneEntitlement.addPromotions;
      entitlementsWithRemovePromotions.push(cloneEntitlement);
    }
  });
  if (entitlementsWithRemovePromotions?.length > 0) {
    // removal process by patch entitlement
    await client.post('/orderV2', { ...requestPayload, entitlements: entitlementsWithRemovePromotions });
    requestPayload?.entitlements?.forEach((entitlement) => {
      delete entitlement.operations;
    });
    // add process by amendment entitlement
    const responseAdd = await client.post('/orderV2', requestPayload);
    return responseAdd.data;
  }

  if (
    requestPayload?.orderType === OrderItemType.REACTIVATION_ORDER &&
    (requestPayload?.entitlements?.[0]?.transferTransactionAccountId ||
      requestPayload?.entitlements?.[0]?.transferInvoiceGroupId)
  ) {
    // Reactivate order
    await client.post('/orderV2', requestPayload);
    if (requestPayload.entitlements[0].transferTransactionAccountId) {
      // Modify the transaction account and invoice group
      const modifyOrderRequestPayload: CustomOrderRequest = {
        ...requestPayload,
        orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
      };
      const modifiedResponseData = await client.post('/orderV2', modifyOrderRequestPayload);
      return modifiedResponseData.data;
    }
    if (requestPayload.entitlements[0].transferInvoiceGroupId) {
      // Modify the invoice group
      const modifiedInvoiceGroupData: CustomOrderRequest = {
        ...requestPayload,
        orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
        invoiceGroupId: requestPayload.entitlements[0].transferInvoiceGroupId,
      };
      const modifiedResponseData = await client.post('/orderV2', modifiedInvoiceGroupData);
      return modifiedResponseData.data;
    }
  }
  const response = await client.post('/orderV2', requestPayload);
  return response.data;
};

// This method is only called from Add/Remove discounts sidebar where only current entitlement is present.
export const updateEntitlementPromotions = async (requestPayload: CustomOrderRequest): Promise<any> => {
  let response;
  if (requestPayload?.entitlements?.[0]?.operations?.length) {
    const cloneRequestPayload = cloneDeep(requestPayload);
    if (cloneRequestPayload.entitlements) {
      delete cloneRequestPayload.entitlements[0]?.addPromotions;
    }
    // removal process by patch entitlement
    response = await client.post('/orderV2', cloneRequestPayload);
  }

  if (requestPayload?.entitlements?.[0]?.addPromotions?.length) {
    delete requestPayload.entitlements[0]?.operations;
    // add process by amendment entitlement
    response = await client.post('/orderV2', requestPayload);
  }
  return response?.data;
};
